import { useApp } from '../store';
import { unfold, computeOutline, computeBendCenterLines } from '../lib/unfold';
import { exportDxf, downloadDxf } from '../lib/dxf';
import { exportStl, downloadStl } from '../lib/stl';
import { exportStep, downloadStep } from '../lib/step';

export default function Toolbar() {
  const part = useApp((s) => s.part);
  const showFlat = useApp((s) => s.showFlat);
  const toggleFlat = useApp((s) => s.toggleFlat);
  const undo = useApp((s) => s.undo);
  const redo = useApp((s) => s.redo);
  const past = useApp((s) => s.past);
  const future = useApp((s) => s.future);
  const saveJson = useApp((s) => s.saveJson);
  const loadJson = useApp((s) => s.loadJson);
  const bumpFit = useApp((s) => s.bumpFit);
  const theme = useApp((s) => s.theme);
  const toggleTheme = useApp((s) => s.toggleTheme);

  const handleExport = () => {
    const layout = unfold(part);
    const segments = computeOutline(layout);
    const centerLines = computeBendCenterLines(layout);
    const dxf = exportDxf(segments, layout.holes, centerLines);
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadDxf(`bendsteelcal_${ts}.dxf`, dxf);
  };

  const handleExportStl = () => {
    const stl = exportStl(part);
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadStl(`bendsteelcal_${ts}.stl`, stl);
  };

  const handleExportStep = () => {
    const stp = exportStep(part);
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadStep(`bendsteelcal_${ts}.stp`, stp);
  };

  const handleLoad = async () => {
    try {
      await loadJson();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load file';
      alert(`Load failed: ${msg}`);
    }
  };

  return (
    <header className="h-12 shrink-0 bg-panel border-b border-line flex items-center px-2 sm:px-4 gap-2 sm:gap-3">
      <div className="font-semibold tracking-tight text-ink whitespace-nowrap">
        <span className="hidden sm:inline">BendSteel<span className="text-accent">Cal</span></span>
        <span className="sm:hidden">B<span className="text-accent">SC</span></span>
      </div>
      <div className="text-xs text-mute hidden lg:block">Sheet metal designer · mm</div>
      <div className="text-[10px] text-mute hidden xl:block ml-2 leading-tight">
        © Sapsricharoen Engineering Co., Ltd.<br />All rights reserved.
      </div>

      <div className="flex items-center gap-1 ml-1 sm:ml-2">
        <button
          className="btn-ghost px-2 disabled:opacity-30 disabled:cursor-not-allowed"
          onClick={undo}
          disabled={past.length === 0}
          title="Undo (Ctrl+Z)"
          aria-label="Undo"
        >
          ↶
        </button>
        <button
          className="btn-ghost px-2 disabled:opacity-30 disabled:cursor-not-allowed"
          onClick={redo}
          disabled={future.length === 0}
          title="Redo (Ctrl+Shift+Z)"
          aria-label="Redo"
        >
          ↷
        </button>
      </div>

      <div className="flex-1" />

      <button
        className="btn-ghost px-2"
        onClick={toggleTheme}
        title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        aria-label="Toggle theme"
      >
        {theme === 'light' ? '☾' : '☀'}
      </button>

      {/* Save / Load / Fit — visible inline on desktop, hidden on mobile to
          keep the toolbar uncluttered. Mobile users still get them via the
          sidebar drawer if we wire them there later, but for now the keyboard
          shortcuts (Ctrl+S/O/F) cover desktop power use. */}
      <div className="hidden sm:flex items-center gap-1">
        <button className="btn" onClick={handleLoad} title="Load JSON (Ctrl+O)">Load</button>
        <button className="btn" onClick={saveJson} title="Save JSON (Ctrl+S)">Save</button>
        {!showFlat && (
          <button className="btn" onClick={bumpFit} title="Fit view to part (F)">
            Fit
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 bg-panel2 border border-line rounded p-0.5">
        <button
          className={`px-3 py-1 rounded text-xs ${!showFlat ? 'bg-accent text-black' : 'text-mute hover:text-ink'}`}
          onClick={() => { if (showFlat) toggleFlat(); }}
        >
          3D
        </button>
        <button
          className={`px-3 py-1 rounded text-xs ${showFlat ? 'bg-accent text-black' : 'text-mute hover:text-ink'}`}
          onClick={() => { if (!showFlat) toggleFlat(); }}
        >
          Flat
        </button>
      </div>

      <button
        className="btn whitespace-nowrap"
        onClick={handleExportStep}
        title="Export 3D solid as STEP (open in Solid Edge / SolidWorks / Fusion / Inventor as a parametric solid body — recommended for editing)"
      >
        <span className="hidden sm:inline">Export STEP</span>
        <span className="sm:hidden">STP</span>
      </button>
      <button
        className="btn whitespace-nowrap"
        onClick={handleExportStl}
        title="Export 3D mesh as STL (lightweight mesh body — use STEP instead when you need to edit the solid)"
      >
        <span className="hidden sm:inline">Export STL</span>
        <span className="sm:hidden">STL</span>
      </button>
      <button className="btn-primary whitespace-nowrap" onClick={handleExport} title="Export DXF flat pattern (best for sheet-metal CAD reconstruction)">
        <span className="hidden sm:inline">Export DXF</span>
        <span className="sm:hidden">DXF</span>
      </button>
    </header>
  );
}
